import {computed, inject, Injectable, Signal, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {CopilotLegacyAdapterService} from './copilot-legacy-adapter.service';
import {PolarisFacadeService} from './polaris-facade.service';
import {PolarisMigrationService} from './polaris-migration.service';
import {
  POLARIS_RUNTIME_STRATEGIES,
  PolarisAccountKey,
  PolarisChatId,
  PolarisChatVm,
  PolarisFacadeContract,
  PolarisFacadeViewState,
  PolarisMessageVm,
  PolarisOpenChatResultVm,
  PolarisPaginationParams,
  PolarisRuntimeFacadeContract,
  PolarisRuntimeInitResult,
  PolarisRuntimeMode,
  PolarisRuntimeModeSelection,
  PolarisMigrationScope,
  PolarisSendMessageOutcomeVm,
  PolarisSendMessageOptionsVm,
  PolarisStartChatOptionsVm,
  PolarisRuntimeStrategy,
  PolarisWizardVm
} from './model';

function parsePolarisRuntimeStrategy(value: unknown): PolarisRuntimeStrategy {
  if (typeof value === 'string' && POLARIS_RUNTIME_STRATEGIES.includes(value as PolarisRuntimeStrategy)) {
    return value as PolarisRuntimeStrategy;
  }
  if (value != null) {
    console.warn(
      `Invalid polarisRuntimeStrategy "${value}". Valid: ${POLARIS_RUNTIME_STRATEGIES.join(', ')}. Using "legacy_only".`
    );
  }
  return 'legacy_only';
}

@Injectable({
  providedIn: 'root'
})
export class PolarisRuntimeFacadeService implements PolarisRuntimeFacadeContract {
  private readonly polarisFacadeService = inject(PolarisFacadeService);
  private readonly copilotLegacyAdapterService = inject(CopilotLegacyAdapterService);
  private readonly polarisMigrationService = inject(PolarisMigrationService);

  private readonly initializedAccountKey = signal<PolarisAccountKey | null>(null);
  private readonly runtimeStrategy = parsePolarisRuntimeStrategy(environment.polarisRuntimeStrategy);
  private runtimeInitInFlight: Promise<PolarisRuntimeInitResult> | null = null;
  private runtimeInitInFlightScopeKey: string | null = null;
  private readonly runtimeModeSelection = signal<PolarisRuntimeModeSelection>({
    ...this.getInitialModeSelection()
  });

  public readonly runtimeMode = computed(() => this.runtimeModeSelection().mode);
  public readonly isUsingLegacyFlow = computed(() => this.runtimeMode() === 'legacy');

  public get state(): PolarisFacadeViewState {
    return this.activeFacade().state;
  }

  public isInitializedForAccount(accountKey: PolarisAccountKey): boolean {
    return this.initializedAccountKey() === accountKey;
  }

  public async init(accountKey: PolarisAccountKey): Promise<void> {
    this.initializedAccountKey.set(accountKey);

    if (this.runtimeStrategy === 'polaris_only') {
      await this.polarisFacadeService.init(accountKey);
      return;
    }

    this.copilotLegacyAdapterService.init(accountKey);

    if (this.runtimeMode() === 'new') {
      await this.polarisFacadeService.init(accountKey);
    }
  }

  public async initPolarisRuntime(scope: PolarisMigrationScope): Promise<PolarisRuntimeInitResult> {
    const scopeKey = this.buildScopeKey(scope);
    if (this.runtimeInitInFlight && this.runtimeInitInFlightScopeKey === scopeKey) {
      return this.runtimeInitInFlight;
    }

    const runtimeInitPromise = this.initializeRuntime(scope);
    this.runtimeInitInFlight = runtimeInitPromise;
    this.runtimeInitInFlightScopeKey = scopeKey;

    try {
      return await runtimeInitPromise;
    } finally {
      if (this.runtimeInitInFlight === runtimeInitPromise) {
        this.runtimeInitInFlight = null;
        this.runtimeInitInFlightScopeKey = null;
      }
    }
  }

  private async initializeRuntime(scope: PolarisMigrationScope): Promise<PolarisRuntimeInitResult> {
    if (this.initializedAccountKey() !== scope.accountKey) {
      await this.init(scope.accountKey);
    }

    if (this.runtimeStrategy !== 'migration') {
      return this.applyStrategyWithoutMigration(scope.accountKey);
    }

    const result = await this.polarisMigrationService.initializeRuntime(scope);

    if (result.modeSelection.mode === 'new') {
      await this.polarisFacadeService.init(scope.accountKey);
    }
    this.applyModeSelection(result.modeSelection);

    return result;
  }

  private buildScopeKey(scope: PolarisMigrationScope): string {
    return `${scope.accountKey}:${scope.userId}`;
  }

  public getChat(chatId: PolarisChatId): PolarisChatVm | undefined {
    return this.activeFacade().getChat(chatId);
  }

  public getChatMessages(chatId: PolarisChatId): Signal<PolarisMessageVm[]> {
    return this.activeFacade().getChatMessages(chatId);
  }

  public async loadChatsOverview(pagination: PolarisPaginationParams = {}): Promise<void> {
    const facade = this.activeFacade();
    await facade.loadChatsOverview(pagination);
  }

  public async openChat(chatId: PolarisChatId): Promise<PolarisOpenChatResultVm> {
    const facade = this.activeFacade();
    return facade.openChat(chatId);
  }

  public async ensureChatLoaded(chatId: PolarisChatId): Promise<boolean> {
    const facade = this.activeFacade();
    return facade.ensureChatLoaded(chatId);
  }

  public async startChat(prompt: string, options?: PolarisStartChatOptionsVm): Promise<PolarisChatId> {
    const facade = this.activeFacade();
    return facade.startChat(prompt, options);
  }

  public async sendMessage(
    chatId: PolarisChatId | null,
    prompt: string,
    options?: PolarisSendMessageOptionsVm
  ): Promise<PolarisSendMessageOutcomeVm> {
    const facade = this.activeFacade();
    return facade.sendMessage(chatId, prompt, options);
  }

  public async loadMoreChats(): Promise<void> {
    const facade = this.activeFacade();
    await facade.loadMoreChats();
  }

  public setUserBrandContext(brand: string | null, competitors: string[]): void {
    // Keep both facades synchronized so context is preserved if mode changes.
    this.copilotLegacyAdapterService.setUserBrandContext(brand, competitors);
    this.polarisFacadeService.setUserBrandContext(brand, competitors);
  }

  public getWizardsResponse(): Observable<PolarisWizardVm[]> {
    return this.activeFacade().getWizardsResponse();
  }

  public setRecentChatId(chatId: PolarisChatId): void {
    this.activeFacade().setRecentChatId(chatId);
  }

  public resetRecentChatId(): void {
    this.activeFacade().resetRecentChatId();
  }

  public async updateChatTitle(chatId: PolarisChatId, newTitle: string): Promise<void> {
    if (this.isUsingLegacyFlow()) {
      return;
    }
    return this.polarisFacadeService.updateChatTitle(chatId, newTitle);
  }

  private applyModeSelection(selection: PolarisRuntimeModeSelection): void {
    this.runtimeModeSelection.set(selection);
  }

  private getInitialModeSelection(): PolarisRuntimeModeSelection {
    if (this.runtimeStrategy === 'polaris_only') {
      return {
        mode: 'new',
        source: 'manual_override',
        selectedAt: Date.now(),
        reason: 'runtime strategy: polaris_only'
      };
    }

    return {
      mode: 'legacy',
      source: this.runtimeStrategy === 'legacy_only' ? 'manual_override' : 'default_legacy',
      selectedAt: Date.now(),
      reason: this.runtimeStrategy === 'legacy_only' ? 'runtime strategy: legacy_only' : undefined
    };
  }

  private async applyStrategyWithoutMigration(accountKey: PolarisAccountKey): Promise<PolarisRuntimeInitResult> {
    const modeSelection: PolarisRuntimeModeSelection = {
      mode: this.runtimeStrategy === 'polaris_only' ? 'new' : 'legacy',
      source: 'manual_override',
      selectedAt: Date.now(),
      reason: `runtime strategy: ${this.runtimeStrategy}`
    };

    this.applyModeSelection(modeSelection);

    if (modeSelection.mode === 'new') {
      await this.polarisFacadeService.init(accountKey);
    } else {
      this.copilotLegacyAdapterService.init(accountKey);
    }

    return {
      modeSelection
    };
  }

  private activeFacade(): PolarisFacadeContract {
    return this.getFacadeForMode(this.runtimeMode());
  }

  private getFacadeForMode(mode: PolarisRuntimeMode): PolarisFacadeContract {
    return mode === 'new' ? this.polarisFacadeService : this.copilotLegacyAdapterService;
  }
}
